/**
 * Time-Based Scaling Utilities
 * Shared logic for time-based x-axis spacing in charts
 * Extracted from LinePlot.jsx for reusability
 */

export const MIN_GAP_RATIO = 0.03; // Minimum gap is 3% of total time span

/**
 * Convert date string (YYYYMMDD) to timestamp
 * @param {string} dateStr - Date in YYYYMMDD format
 * @returns {number} Unix timestamp in milliseconds
 */
export function convertDateToTimestamp(dateStr) {
    try {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        return new Date(year, month - 1, day).getTime();
    } catch (e) {
        console.warn('Invalid date format:', dateStr);
        return Date.now(); // fallback
    }
}

/**
 * Apply minimum gap constraint to time-based data using ratio-based approach
 * @param {Array} dataWithTimestamps - Array of objects with timestamp property
 * @returns {Array} Array with adjustedTimestamp property added to each object
 */
export function applyMinimumGapConstraint(dataWithTimestamps) {
    const adjustedData = [];

    if (dataWithTimestamps.length === 0) {
        return adjustedData;
    }

    // Sort by timestamp to ensure chronological order
    const sortedData = [...dataWithTimestamps].sort((a, b) => a.timestamp - b.timestamp);

    // First point keeps its original timestamp
    adjustedData.push({ ...sortedData[0], adjustedTimestamp: sortedData[0].timestamp });

    if (sortedData.length === 1) {
        return adjustedData;
    }

    // Calculate total time span
    const firstTimestamp = sortedData[0].timestamp;
    const lastTimestamp = sortedData[sortedData.length - 1].timestamp;
    const totalTimeSpan = lastTimestamp - firstTimestamp;

    // If all points have same timestamp, just return them
    if (totalTimeSpan === 0) {
        return sortedData.map(d => ({ ...d, adjustedTimestamp: d.timestamp }));
    }

    // Calculate minimum gap based on ratio of total span
    const minGapMs = totalTimeSpan * MIN_GAP_RATIO;

    // Adjust subsequent points to enforce minimum distance
    let totalBorrowed = 0;
    const gaps = [];

    for (let i = 1; i < sortedData.length; i++) {
        const prev = sortedData[i - 1];
        const current = sortedData[i];
        const actualGap = current.timestamp - prev.timestamp;

        if (actualGap < minGapMs) {
            // This gap is too small, needs borrowing
            gaps.push({ index: i, actualGap, needsMin: true });
            totalBorrowed += (minGapMs - actualGap);
        } else {
            gaps.push({ index: i, actualGap, needsMin: false });
        }
    }

    // Redistribute borrowed space from larger gaps
    const gapsAboveMin = gaps.filter(g => !g.needsMin);
    const totalAvailable = gapsAboveMin.reduce((sum, g) => sum + (g.actualGap - minGapMs), 0);

    let cumulativeAdjusted = adjustedData[0].adjustedTimestamp;

    for (let i = 1; i < sortedData.length; i++) {
        const gap = gaps[i - 1];
        let adjustedGap;

        if (gap.needsMin) {
            // Apply minimum gap
            adjustedGap = minGapMs;
        } else {
            // Shrink proportionally to redistribute borrowed space
            const shrinkAmount = totalAvailable > 0 ? ((gap.actualGap - minGapMs) / totalAvailable) * totalBorrowed : 0;
            adjustedGap = gap.actualGap - shrinkAmount;
            adjustedGap = Math.max(adjustedGap, minGapMs); // Never go below minimum
        }

        cumulativeAdjusted += adjustedGap;
        adjustedData.push({
            ...sortedData[i],
            adjustedTimestamp: cumulativeAdjusted
        });
    }

    return adjustedData;
}
